import React, {ReactElement} from "react";
import PropTypes from "prop-types";

import { useTooltipTriggerState } from "react-stately";
import { mergeProps, useTooltip, useTooltipTrigger, TooltipTriggerProps, AriaTooltipProps } from "react-aria";

interface TooltipTextProps extends AriaTooltipProps {
    location: string;
    state: any;
    children: string;
}
function TooltipText({ state, location, ...props }: TooltipTextProps) {
    let { tooltipProps } = useTooltip(props, state);
    return (
        <p
            className={`pt-tooltiptext ${state.isOpen ? "show" : ""} ${location}`}
            {...mergeProps(props, tooltipProps)}
        >
            {props.children}
        </p>
    );
}

export interface TooltipProps extends TooltipTriggerProps {
    children: ReactElement,
    text: string,
    left?: boolean,
    right?: boolean,
    bottom?: boolean,
    delay?: number,

}

export const Tooltip = (props: TooltipProps) => {
    const { text, left, right, bottom, children } = props;
    const location = left ? "left" : right ? "right" : bottom ? "bottom" : "top";

    let state = useTooltipTriggerState(props);
    let ref = React.useRef();

    // Get props for the trigger and its tooltip
    let { triggerProps, tooltipProps } = useTooltipTrigger(props, state, ref);

    return (
    <span className={"pt-tooltip"} style={{ position: "relative" }}>
      <span className="child" ref={ref} {...triggerProps}>
          {children}
          <TooltipText
              location={location}
              state={state}
              {...tooltipProps}
          >
            {text}
          </TooltipText>
      </span>
      <style>{`
        .pt-tooltip {
          display: flex;
          align-self: flex-start;
          flex: 0;
        }

        .pt-tooltip .child {
          position: relative;
          display: inline-block;
          float: left;
          width: fit-content;
        }

        .pt-tooltiptext.show {
          visibility: visible;
          opacity: 1;
          transition: opacity 0.2s ease-in-out;
          display: block;
        }

        .pt-tooltiptext {
          background-color: var(--white);
          border: 1px solid var(--black);
          color: var(--black);
          text-align: center;
          border-radius: 6px;
          padding: 2px 18px 5px;
          position: absolute;
          z-index: 1;
          white-space: nowrap;
          box-shadow: 0px 2px 4px var(--black-opaque-32);
          opacity: 0;
          display: none;
          transition: opacity 0.15s ease-in-out;
          will-change: transform;
        }

        .pt-tooltiptext.bottom {
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          margin-top: 10px;
        }

        .pt-tooltiptext.top {
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          margin-bottom: 10px;
        }

        .pt-tooltiptext.left {
          right: 100%;
          top: 50%;
          transform: translateY(-50%);
          margin: 0px 10px 0px 0px;
        }

        .pt-tooltiptext.right {
          left: 100%;
          top: 50%;
          transform: translateY(-50%);
          margin: 0px 0px 0px 10px;
        }

        .pt-tooltiptext.top::after {
          content: "";
          position: absolute;
          top: 100%;
          left: 50%;
          margin-left: -7px;
          border-width: 7px;
          border-style: solid;
          border-color: var(--black) transparent transparent transparent;
        }

        .pt-tooltiptext.bottom::after {
          content: "";
          position: absolute;
          bottom: 100%;
          left: 50%;
          margin-left: -7px;
          border-width: 7px;
          border-style: solid;
          border-color: transparent transparent var(--black) transparent;
        }

        .pt-tooltiptext.left::after {
          content: "";
          position: absolute;
          top: 50%;
          left: 100%; /* To the right of the tooltip */
          margin-top: -5px;
          border-width: 5px;
          border-style: solid;
          border-color: transparent transparent transparent var(--black);
        }

        .pt-tooltiptext.right::after {
          content: "";
          position: absolute;
          top: 50%;
          right: 100%; /* To the left of the tooltip */
          margin-top: -5px;
          border-width: 5px;
          border-style: solid;
          border-color: transparent var(--black) transparent transparent;
        }
      `}</style>
    </span>
    );
};
Tooltip.propTypes = {
    text: PropTypes.string.isRequired,
    left: PropTypes.bool,
    right: PropTypes.bool,
    bottom: PropTypes.bool,
    delay: PropTypes.number
};

Tooltip.defaultProps = {
    left: false,
    right: false,
    bottom: false,
    delay: 0
};
